// The ? panel (design S10): the help page of the screen in front, beside the screen, so it can stay open
// while working. Missing pages say so and offer the User Guide; help never blocks the screen.

import React, { useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { helpService, type HelpPage } from 'api/services/helpService';
import HelpMarkdown from './HelpMarkdown';

interface Props {
  pageId: string | null;
  onClose: () => void;
  onOpenCentre: (pageId?: string) => void;
}

const HelpPanel: React.FC<Props> = ({ pageId, onClose, onOpenCentre }) => {
  const [current, setCurrent] = useState<string | null>(pageId);
  const [page, setPage] = useState<HelpPage | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');

  useEffect(() => { setCurrent(pageId); }, [pageId]);

  useEffect(() => {
    if (!current) return;
    let cancelled = false;
    setState('loading');
    helpService.getPage(current)
      .then((p) => { if (!cancelled) { setPage(p); setState(p ? 'ready' : 'missing'); } })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [current]);

  if (!pageId) return null;

  return (
    <aside className="help-panel" aria-label="Help">
      <div className="help-panel-header">
        <span className="help-panel-title">
          {state === 'ready' && page ? page.title : 'Help'}
          {state === 'ready' && page?.draft && <span className="badge badge-warning help-draft">Draft</span>}
        </span>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => onOpenCentre(current ?? undefined)} title="Open the User Guide">
          User Guide
        </button>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close help"><IconSystem.x size={18} /></button>
      </div>
      <div className="help-panel-body">
        {state === 'loading' && <div className="config-loading">Loading...</div>}
        {state === 'missing' && (
          <div className="setup-sheet-note">
            There is no help page for this screen yet. The <button type="button" className="btn btn-link btn-sm" onClick={() => onOpenCentre()}>User Guide</button> has the others.
          </div>
        )}
        {state === 'error' && <div className="setup-sheet-note">Help is not available right now. The screen works as usual.</div>}
        {state === 'ready' && page && <HelpMarkdown markdown={page.markdown} onOpenPage={setCurrent} />}
      </div>
    </aside>
  );
};

export default HelpPanel;
